import { BRAND } from "@/data/products";
import { Instagram, Phone, Mail, MapPin } from "lucide-react";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-ink-deep text-cream-100 pt-20 pb-8 border-t border-gold/25"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-3 gap-12 pb-14 border-b border-cream-100/10">
          <div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute inset-0 rounded-full border border-gold/50 scale-110" />
                <img
                  src={BRAND.logo}
                  alt="Vardaan Arts"
                  className="relative w-20 h-20 object-contain"
                />
              </div>
              <div>
                <div className="font-serif text-2xl tracking-[0.14em] text-cream-100 font-semibold">
                  VARDAAN ARTS
                </div>
                <div className="text-[10px] tracking-[0.32em] uppercase text-gold-light mt-2 font-medium">
                  Crafting Divine Beauty
                </div>
              </div>
            </div>
            <p className="text-cream-100/60 text-sm mt-8 leading-relaxed max-w-sm">
              Handcrafted Makrana marble temples, statues, fountains and
              sculptures. Custom pre-order · Worldwide shipping · Since{" "}
              {BRAND.established}.
            </p>
          </div>

          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-gold-light mb-4">
              Collections
            </div>
            <ul className="space-y-2 text-cream-100/70 text-sm">
              {[
                "Indoor Home Temple",
                "Outdoor Temples",
                "Statues",
                "Fountains",
                "Wall Panels",
                "Inlay Work Art",
                "Wash Basins",
                "Marble Table Tops",
              ].map((c) => (
                <li key={c}>
                  <a href="/#collections" className="hover:text-gold-light">
                    {c}
                  </a>
                </li>
              ))}
              <li className="pt-2 border-t border-cream-100/10 mt-2">
                <a href="/blog" className="hover:text-gold-light font-medium">
                  → Vardaan Journal (Blog)
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-gold-light mb-4">
              Contact
            </div>
            <ul className="space-y-3 text-cream-100/70 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-gold-light mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-gold-light/70 text-[10px] tracking-[0.2em] uppercase mb-1">Studio · Thane</div>
                  <a href={`tel:${BRAND.phone}`} className="hover:text-gold-light">
                    {BRAND.phoneDisplay}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-gold-light mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-gold-light/70 text-[10px] tracking-[0.2em] uppercase mb-1">Factory · Makrana</div>
                  <a href={`tel:${BRAND.factoryPhone}`} className="hover:text-gold-light">
                    {BRAND.factoryPhoneDisplay}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-gold-light mt-0.5 flex-shrink-0" />
                <a
                  href={`mailto:${BRAND.email}`}
                  className="hover:text-gold-light"
                >
                  {BRAND.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gold-light mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-gold-light/70 text-[10px] tracking-[0.2em] uppercase mb-1">Studio · Thane</div>
                  <span>{BRAND.address}</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gold-light mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-gold-light/70 text-[10px] tracking-[0.2em] uppercase mb-1">Factory · Makrana</div>
                  <span>{BRAND.factoryAddress}</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Instagram className="w-4 h-4 text-gold-light mt-0.5 flex-shrink-0" />
                <a
                  href={BRAND.instagram}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-gold-light"
                >
                  @vardaan.arts
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* SEO keyword strip — natural search phrases */}
        <div className="pt-8 pb-2 border-t border-gold/15 mt-8">
          <p className="text-[11px] leading-relaxed text-cream-100/40 tracking-wide">
            Vardaan Arts — custom marble temple manufacturer in Makrana,
            Rajasthan. Marble temple for home, designer marble pooja mandir,
            white marble God statues & moorti online, hand-carved Ram Darbar,
            Radha Krishna, Ganesha & Shiva Parivar statues, Jain temple
            parikar, marble fountains, wall panels & Roman art sculptures.
            Export-quality Makrana sangmarmar temples with worldwide shipping
            for homes, flats & NRIs across USA, UK, Canada, Australia & the
            Gulf.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 text-xs text-cream-100/50">
          <div>
            © {new Date().getFullYear()} Vardaan Arts. All rights reserved. Founded by{" "}
            {BRAND.owner}.
          </div>
          <div className="font-serif italic text-gold-light">
            {BRAND.tagline}
          </div>
        </div>
      </div>
    </footer>
  );
}
